import { entitlementHolds } from "../entitlements/policy";
import { freePlan, paidPlan } from "./constants";
import type { PaymentPlan } from "./types";

export interface BusinessPlanInput {
  id?: string;
  subscriptionStatus?: string | null;
  stripeCancelAt?: string | null;
  transactionLimitCents?: number | null;
}

/**
 * The `BillingAccount` fields the plan is resolved from.
 *
 * Provider-agnostic on purpose: `periodEnd` is whatever rail sold the
 * subscription, where `BusinessPlanInput.stripeCancelAt` can only ever describe
 * a Stripe one. An Apple or Google plan has an end date too, and reading it out
 * of a Stripe-named column is how it would end up wrong.
 */
export interface BillingAccountInput {
  state?: string | null;
  plan?: string | null;
  periodEnd?: string | null;
}

type ErrorLogger = {
  error: (msg: string, ctx: Record<string, unknown>) => void;
} | null;

function parseExpiry(
  value: string | null | undefined,
  businessId: string | undefined,
  errorLogger: ErrorLogger,
): Date | null {
  if (!value) return null;
  try {
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      errorLogger?.error("Failed to parse plan expiry date", {
        businessId,
        cancelAtValue: value,
      });
      return null;
    }
    return parsed;
  } catch (e) {
    errorLogger?.error("Failed to parse plan expiry date", {
      businessId,
      cancelAtValue: value,
      e,
    });
    return null;
  }
}

/**
 * The plan, transaction limit, and expiry for a merchant.
 *
 * Prefers the `BillingAccount` projection when one is supplied, and falls back
 * to the legacy `BusinessProfile` columns when it is not. The fallback is not a
 * deprecation shim: callers that have not yet been migrated pass no projection,
 * and until `refactor-billing-core` is on, the columns are the authority anyway.
 *
 * Two things change when the projection is present.
 *
 * The plan is **read rather than inferred**. The legacy path treats `active` and
 * `trialing` as paid and everything else as free, which silently reads any
 * status outside that pair — including ones a store rail will produce — as the
 * free plan. The projection states its plan outright, and {@link isEntitled}
 * decides whether its standing grants it.
 *
 * Expiry comes from `periodEnd` rather than `stripeCancelAt`. That is what makes
 * the helper provider-agnostic, and it is the reason this matters before IAP
 * ships rather than after.
 *
 * A merchant with no projection row, or one carrying neither state nor plan,
 * falls back to the legacy columns rather than being forced to free — absent is
 * "nothing to say", and the columns still hold a real answer for them.
 */
export const getBusinessPlan = (
  businessProfile: BusinessPlanInput | null | undefined,
  errorLogger: ErrorLogger,
  billingAccount?: BillingAccountInput | null,
): PaymentPlan => {
  if (billingAccount?.state && billingAccount?.plan) {
    const entitled = entitlementHolds(billingAccount, Date.now());
    const base = entitled ? paidPlan : freePlan;

    return {
      ...base,
      transactionLimit:
        businessProfile?.transactionLimitCents ?? base.transactionLimit,
      // Only a paid plan has a period to expire at; a free merchant showing an
      // expiry date would be describing a subscription they do not have.
      planExpiresAt: entitled
        ? parseExpiry(
            billingAccount.periodEnd,
            businessProfile?.id,
            errorLogger,
          )
        : null,
    };
  }

  const planExpiresAt = parseExpiry(
    businessProfile?.stripeCancelAt,
    businessProfile?.id,
    errorLogger,
  );

  if (
    businessProfile?.subscriptionStatus === "active" ||
    businessProfile?.subscriptionStatus === "trialing"
  ) {
    return {
      ...paidPlan,
      transactionLimit:
        businessProfile.transactionLimitCents ?? paidPlan.transactionLimit,
      planExpiresAt,
    };
  }

  return {
    ...freePlan,
    transactionLimit:
      businessProfile?.transactionLimitCents ?? freePlan.transactionLimit,
  };
};
